import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { LoopDto, RunDto, StageAttemptDto } from '@aiai/shared';
import { Callout } from '../../components/Callout';
import { SectionCard } from '../../components/SectionCard';
import { StatusBadge } from '../../components/StatusBadge';
import { PipelineStepper } from '../../components/PipelineStepper';
import { ScorecardPanel } from '../../components/ScorecardPanel';
import { useWorkspacePath } from '../../hooks/useWorkspacePath';
import { formatCost, formatDateTime } from '../../lib/format';

function MetaItem({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-2xs font-medium uppercase tracking-wide text-ink-faint">
        {label}
      </dt>
      <dd className="mt-0.5 break-words text-sm text-ink">{children}</dd>
    </div>
  );
}

/**
 * The run detail page's right rail: scorecard, pipeline stepper, the details
 * meta grid, token-usage totals, and the iterations list. Read-only — every
 * value derives from `run` (token totals memoized here) plus a little query data.
 */
export function RunSidebar({
  run,
  isTerminal,
  attempts,
  attemptsError,
  loop,
  loopId,
  projectRef,
}: {
  run: RunDto;
  isTerminal: boolean;
  attempts: StageAttemptDto[] | undefined;
  attemptsError: boolean;
  loop: LoopDto | null | undefined;
  loopId: string | null;
  projectRef: string;
}) {
  const { wp } = useWorkspacePath();

  const tokenTotals = useMemo(() => {
    let input = 0, output = 0, cacheCreate = 0, cacheRead = 0, any = false;
    for (const s of run.stages) {
      if (s.inputTokens != null) { input += s.inputTokens; any = true; }
      if (s.outputTokens != null) { output += s.outputTokens; any = true; }
      if (s.cacheCreationTokens != null) { cacheCreate += s.cacheCreationTokens; any = true; }
      if (s.cacheReadTokens != null) { cacheRead += s.cacheReadTokens; any = true; }
    }
    return any ? { input, output, cacheCreate, cacheRead } : null;
  }, [run]);

  return (
    <div className="space-y-6">
      {isTerminal && run.scorecard && (
        <SectionCard title="Scorecard" bodyClassName="p-4">
          <ScorecardPanel scorecard={run.scorecard} />
        </SectionCard>
      )}

      <SectionCard title="Pipeline" bodyClassName="p-4">
        <PipelineStepper
          stages={run.stages}
          plannedRoles={run.pipeline}
          attempts={attempts}
        />
        {attemptsError && (
          <Callout tone="danger">Could not load attempt details.</Callout>
        )}
      </SectionCard>

      <SectionCard title="Details" bodyClassName="p-4">
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <MetaItem label="Risk">
            {run.riskLevel === 'low' ? (
              <span className="font-semibold text-success-300">LOW</span>
            ) : run.riskLevel === 'high' ? (
              <span className="font-semibold text-danger-300">HIGH</span>
            ) : (
              '—'
            )}
          </MetaItem>
          <MetaItem label="Branch">
            {run.branch ? (
              <span className="font-mono text-xs">{run.branch}</span>
            ) : (
              '—'
            )}
          </MetaItem>
          {loop && loopId && (
            <MetaItem label="Loop">
              <Link
                to={wp(`/projects/${projectRef}/loops/${loopId}/edit`)}
                className="text-signal hover:underline"
              >
                {loop.name}
              </Link>
            </MetaItem>
          )}
          <MetaItem label="Started">
            <span className="text-xs">{formatDateTime(run.startedAt)}</span>
          </MetaItem>
          <MetaItem label="Finished">
            <span className="text-xs">
              {formatDateTime(run.finishedAt)}
            </span>
          </MetaItem>
          {run.peakMemoryMb != null && (
            <MetaItem label="Peak memory">
              <span className="font-mono text-xs tabular-nums">
                {run.peakMemoryMb} MiB
                {run.oomKilled && (
                  <span className="ml-2 rounded-[3px] bg-danger-soft px-1.5 py-0.5 text-[0.5625rem] font-bold uppercase tracking-wide text-danger">
                    OOM killed
                  </span>
                )}
              </span>
            </MetaItem>
          )}
          {run.minHostFreeMb != null && (
            <MetaItem label="Host free (min)">
              <span className="font-mono text-xs tabular-nums">
                {run.minHostFreeMb} MiB
              </span>
            </MetaItem>
          )}
          {run.totalCostUsd != null && run.totalCostUsd > 0 && (
            <MetaItem label="Cost">
              <span className="font-mono text-xs tabular-nums">
                {formatCost(run.totalCostUsd)}
              </span>
            </MetaItem>
          )}
        </dl>
      </SectionCard>

      {tokenTotals && (
        <SectionCard title="Token usage" bodyClassName="p-4">
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <MetaItem label="Input">
              <span className="font-mono text-xs tabular-nums">
                {tokenTotals.input.toLocaleString()}
              </span>
            </MetaItem>
            <MetaItem label="Output">
              <span className="font-mono text-xs tabular-nums">
                {tokenTotals.output.toLocaleString()}
              </span>
            </MetaItem>
            {tokenTotals.cacheCreate > 0 && (
              <MetaItem label="Cache write">
                <span className="font-mono text-xs tabular-nums">
                  {tokenTotals.cacheCreate.toLocaleString()}
                </span>
              </MetaItem>
            )}
            {tokenTotals.cacheRead > 0 && (
              <MetaItem label="Cache read">
                <span className="font-mono text-xs tabular-nums">
                  {tokenTotals.cacheRead.toLocaleString()}
                </span>
              </MetaItem>
            )}
            <MetaItem label="Total">
              <span className="font-mono text-xs tabular-nums font-semibold">
                {(tokenTotals.input + tokenTotals.output + tokenTotals.cacheCreate + tokenTotals.cacheRead).toLocaleString()}
              </span>
            </MetaItem>
          </dl>
        </SectionCard>
      )}

      {run.childRuns.length > 0 && (
        <SectionCard title="Iterations" bodyClassName="p-4">
          <ul className="space-y-2">
            {run.childRuns.map((child, i) => (
              <li key={child.id} className="flex items-center justify-between gap-2">
                <Link
                  to={wp(`/runs/${child.id}`)}
                  className="font-mono text-xs text-signal hover:underline truncate"
                >
                  #{i + 1} run {child.id.slice(0, 8)}
                </Link>
                <StatusBadge status={child.status} size="sm" />
              </li>
            ))}
          </ul>
        </SectionCard>
      )}
    </div>
  );
}
